import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, fromEvent, merge, of, Subject, timer } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { switchMap } from "rxjs/operators";

const TOKEN_STORAGE_KEY = 'token';

export interface UserProperties {
  id: string;
  name: string;
  folderName: string;
  canDownload: boolean;
  canUpload: boolean;
  isAdmin: boolean;
  creatorName: string;
}

interface TokenEvent {
  token: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private accessToken$ = new Subject<TokenEvent>();
  public authenticatedUser$ = new BehaviorSubject<UserProperties | null>(null);

  constructor(
    private jwtHelper: JwtHelperService
  ) {
    this.setupExpireTokenOnTimer();
    this.setupTokenValidateInvalidateEvents();
    this.loadExistingToken();
  }

  public setAccessToken(accessToken: string) {
    this.accessToken$.next({ token: accessToken });
  }

  public invalidateAccessToken() {
    this.accessToken$.next({ token: null });
  }

  private loadExistingToken() {
    const storedToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (storedToken !== null) {
      this.accessToken$.next({ token: storedToken });
    }
  }

  private setupTokenValidateInvalidateEvents() {
    this.accessToken$.subscribe((event) => {
      if (event.token !== null) {
        sessionStorage.setItem(TOKEN_STORAGE_KEY, event.token);

        const decodedToken = this.jwtHelper.decodeToken(event.token);
        const userProperties: UserProperties = {
          id: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
          name: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
          folderName: decodedToken['folder'],
          canDownload: decodedToken['rights'] === 'Download',
          canUpload: decodedToken['rights'] === 'Upload',
          isAdmin: decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] === 'Admin',
          creatorName: decodedToken['creatorName']
        };

        this.authenticatedUser$.next(userProperties);
      } else {
        sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        this.authenticatedUser$.next(null);
      }
    });
  }

  private setupExpireTokenOnTimer() {
    this.accessToken$
      .pipe(
        filter((event: TokenEvent): event is { token: string; } => event.token !== null),
        switchMap((event) => {
          const tokenExpiry = this.jwtHelper.getTokenExpirationDate(event.token);

          if (tokenExpiry === null || this.jwtHelper.isTokenExpired(event.token)) {
            return of();
          }

          return timer(tokenExpiry);
        })
      ).subscribe(() => this.invalidateAccessToken());
  }
}
